import { Helmet } from "react-helmet-async";
import { useI18n } from "@/i18n";
import { localizedHead } from "@/i18n/headTranslations";
import { HreflangTags } from "@/components/HreflangTags";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { ReservedImageSlot } from "@/components/bath/ReservedImageSlot";
import { SwipeableGallery } from "@/components/bath/SwipeableGallery";
import { BathLeadForm } from "@/components/bath/BathLeadForm";
import { LocalizedLink } from "@/components/LocalizedLink";
import {
  CheckCircle2,
  ShieldCheck,
  Truck,
  Layers,
  Building2,
  Ruler,
  Hotel,
  Home,
  Wrench,
  Sparkles,
  TrendingUp,
} from "lucide-react";

/**
 * /:lang/bath-sanitary/aria-sculpt
 * High-converting, SEO-optimized product page for the ARIA Sculpt
 * freestanding solid surface bathtub. Built for the developer / designer lane.
 */
const AriaSculpt = () => {
  const metaTitle = "ARIA Sculpt Fluted Freestanding Bathtub | Solid Surface";
  const metaDescription =
    "Architectural fluted freestanding bathtub in solid surface stone resin. CE certified. Engineered for primary suites, branded residences and 5★ hotels.";
  const canonical = "https://www.aureum7trading.com/en/bath-sanitary/aria-sculpt";
  const siteOrigin = "https://www.aureum7trading.com";
  const { language } = useI18n();
  const head = localizedHead(language, "/bath-sanitary/aria-sculpt", { title: metaTitle, description: metaDescription });

  const galleryImages = [
    {
      src: "/images/bath/aria-sculpt-hero.jpg",
      alt: "ARIA Sculpt freestanding solid surface bathtub — modern luxury bathroom hero shot",
      title: "ARIA Sculpt Freestanding Bathtub — Hero",
      caption: "Hero · Freestanding silhouette",
      description:
        "Front-facing hero of the ARIA Sculpt 1700 mm freestanding solid surface bathtub in matte white, set in a contemporary luxury bathroom.",
    },
    {
      src: "/images/bath/aria-sculpt-detail.jpg",
      alt: "ARIA Sculpt freestanding solid surface bathtub fluted exterior detail — seamless cast finish",
      title: "ARIA Sculpt — Fluted Exterior Detail",
      caption: "Detail · Fluted exterior",
      description:
        "Close-up detail of the ARIA Sculpt freestanding bathtub in matte white, showing its vertical fluted exterior and seamless cast solid surface rim.",
    },
    {
      src: "/images/bath/aria-sculpt-noir.jpg",
      alt: "ARIA Sculpt Noir matte black freestanding solid surface bathtub side view",
      title: "ARIA Sculpt Noir — Matte Black Finish",
      caption: "Finish · Matte black noir",
      description:
        "ARIA Sculpt in the matte black noir finish — a sculptural freestanding solid surface bathtub for darker, editorial bathroom palettes.",
    },
    {
      src: "/images/bath/aria-sculpt-noir-top.jpg",
      alt: "ARIA Sculpt Noir matte black bathtub top-down studio view with chrome drain",
      title: "ARIA Sculpt Noir — Top View",
      caption: "Studio · Noir top view",
      description:
        "Top-down studio view of the ARIA Sculpt Noir in matte black, highlighting the seamless interior and polished chrome centre drain.",
    },
  ];


  const specs = [
    { label: "Dimensions", value: "1700 × 800 × 580 mm (L × W × H)" },
    { label: "Material", value: "Cast Solid Surface (Stone Resin Composite)" },
    { label: "Finish", value: "Matte White (Gloss available on request)" },
    { label: "Installation", value: "Freestanding · Floor-mounted" },
    { label: "Weight", value: "≈ 95 kg (empty)" },
    { label: "Capacity", value: "≈ 240 L to overflow" },
    { label: "Drainage", value: "Concealed centre pop-up · Chrome / Brushed Gold" },
    { label: "Certification", value: "CE Marked · EN 14516 compliant" },
    { label: "Warranty", value: "5 years structural · 2 years hardware" },
    { label: "MOQ (Container)", value: "Bulk pricing from 20+ units" },
  ];

  const whyChoose = [
    {
      icon: TrendingUp,
      title: "Higher perceived value per unit",
      body: "A sculpted freestanding tub instantly elevates listing photography, show-flat impact, and resale appeal — for a fraction of European-brand cost.",
    },
    {
      icon: ShieldCheck,
      title: "Solid surface durability",
      body: "Non-porous, repairable, warm to the touch. Outperforms acrylic on stain resistance and longevity in high-traffic residential and hospitality use.",
    },
    {
      icon: Wrench,
      title: "Specifier-friendly installation",
      body: "Freestanding floor mount, concealed drain, standard EU plumbing rough-in. No structural reinforcement required for typical concrete slabs.",
    },
    {
      icon: Layers,
      title: "Reliable bulk supply",
      body: "MOQ-ready from 20 units. Predictable lead times for phased developments and multi-floor rollouts.",
    },
    {
      icon: Sparkles,
      title: "Designer-grade silhouette",
      body: "Precision-machined fluting and a seamless monolithic form. Reads as bespoke joinery, not catalogue plumbing.",
    },
    {
      icon: CheckCircle2,
      title: "Margin-protective pricing",
      body: "Trade pricing structured to protect your project margin — luxury aesthetic without the European brand premium.",
    },
  ];

  const perfectFor = [
    { icon: Home, title: "Apartments", body: "Branded residences and high-end rental developments seeking signature bathrooms." },
    { icon: Building2, title: "Villas", body: "Private villas and signature primary suites where the tub is the focal point." },
    { icon: Hotel, title: "Hotels", body: "Boutique hotels and serviced apartments needing repeatable, photographable luxury." },
    { icon: Wrench, title: "Renovations", body: "Bathroom renovations and refurbishment projects targeting a premium reposition." },
  ];

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100">
      <HreflangTags currentPath="/bath-sanitary/aria-sculpt" />
      <Helmet>
        <title>{head.title}</title>
        <meta name="description" content={head.description} />
        <meta property="og:title" content={head.title} />
        <meta property="og:description" content={head.description} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={`${siteOrigin}${galleryImages[0].src}`} />
        <meta property="og:image:alt" content={galleryImages[0].alt} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="1500" />
        {galleryImages.slice(1).map((g) => (
          <meta key={g.src} property="og:image" content={`${siteOrigin}${g.src}`} />
        ))}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={head.title} />
        <meta name="twitter:description" content={head.description} />
        <meta name="twitter:image" content={`${siteOrigin}${galleryImages[0].src}`} />
        <meta name="twitter:image:alt" content={galleryImages[0].alt} />

        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "ARIA Sculpt Freestanding Solid Surface Bathtub",
          description: metaDescription,
          sku: "ARIA-SCULPT-1700",
          brand: { "@type": "Brand", name: "Aureum VII Trading" },
          category: "Bathtubs > Freestanding > Solid Surface",
          material: "Solid Surface (Stone Resin)",
          image: galleryImages.map((g) => ({
            "@type": "ImageObject",
            contentUrl: `${siteOrigin}${g.src}`,
            url: `${siteOrigin}${g.src}`,
            name: g.title,
            caption: g.caption,
            description: g.description,
          })),
          offers: {
            "@type": "Offer",
            url: canonical,
            priceCurrency: "EUR",
            price: 600,
            priceValidUntil: "2026-12-31",
            availability: "https://schema.org/InStock",
            businessFunction: "https://schema.org/Sell",
            itemCondition: "https://schema.org/NewCondition",
            eligibleQuantity: { "@type": "QuantitativeValue", minValue: 20, unitCode: "C62" },
            seller: { "@type": "Organization", name: "Aureum VII Trading" },
          },
        })}</script>

        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ImageGallery",
          name: "ARIA Sculpt Freestanding Bathtub Gallery",
          url: `${canonical}#gallery`,
          associatedMedia: galleryImages.map((g) => ({
            "@type": "ImageObject",
            contentUrl: `${siteOrigin}${g.src}`,
            url: `${siteOrigin}${g.src}`,
            name: g.title,
            caption: g.caption,
            description: g.description,
            representativeOfPage: g.src.includes("hero"),
            license: canonical,
            acquireLicensePage: `${siteOrigin}/en/contact`,
            creditText: "Aureum VII Trading",
            copyrightNotice: "© Aureum VII Trading",
          })),
        })}</script>
      </Helmet>

      <Navigation />

      {/* BREADCRUMB */}
      <div className="border-b border-amber-500/10 bg-black/40 pt-28">
        <div className="container mx-auto px-6 py-4 text-xs text-neutral-500 lg:px-12">
          <LocalizedLink to="/bath-sanitary" className="hover:text-amber-300">
            Bath &amp; Sanitary
          </LocalizedLink>
          <span className="mx-2">/</span>
          <span className="text-neutral-300">ARIA Sculpt Bathtub</span>
        </div>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-amber-500/10 py-16">
        <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_30%_20%,hsl(45_70%_50%/0.15),transparent_50%)]" />
        <div className="container relative mx-auto grid gap-12 px-6 lg:grid-cols-2 lg:items-center lg:px-12">
          <div>
            <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-amber-400/80">
              Project Solutions · Freestanding Bathtub
            </p>
            <h1 className="font-serif text-4xl leading-tight text-neutral-50 sm:text-5xl lg:text-6xl">
              Modern Freestanding{" "}
              <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-200 bg-clip-text text-transparent">
                Solid Surface Bathtub
              </span>{" "}
              — Luxury Bathroom Design
            </h1>
            <p className="mt-6 max-w-xl text-lg text-neutral-300">
              Sculpted silhouette. Stone-resin warmth. Specified for developers and designers
              who want a five-star bathroom finish without five-star line items.
            </p>

            {/* Premium summary */}
            <div className="mt-6 rounded-sm border border-amber-500/20 bg-black/30 p-5">
              <p className="text-neutral-200">
                A freestanding solid surface bathtub engineered as the focal point of the room.
                Architectural form, hospitality-grade durability, and trade pricing built for
                multi-unit developments.
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                asChild
                size="lg"
                className="bg-gradient-to-r from-amber-500 to-amber-600 text-black hover:from-amber-400 hover:to-amber-500"
              >
                <a href="#quote">Request Project Pricing</a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-amber-500/40 bg-transparent text-amber-200 hover:bg-amber-500/10 hover:text-amber-100"
              >
                <a href="#specs">View Full Specifications</a>
              </Button>
            </div>
            <p className="mt-6 text-xs text-neutral-500">
              CE Certified · Solid Surface · MOQ-ready from 20 units
            </p>
          </div>
          <div className="relative">
            <ReservedImageSlot
              src="/images/bath/aria-sculpt-hero.jpg"
              alt="ARIA Sculpt modern freestanding solid surface bathtub for luxury bathroom design"
              aspect="aspect-[4/5]"
              slotLabel="Hero — ARIA Sculpt freestanding bathtub"
              priority
              className="shadow-[0_30px_80px_-20px_rgba(245,158,11,0.25)]"
            />
          </div>
        </div>
      </section>

      {/* LONG-FORM DESCRIPTION */}
      <section className="border-b border-amber-500/10 py-20">
        <div className="container mx-auto grid gap-12 px-6 lg:grid-cols-3 lg:px-12">
          <div className="lg:col-span-2">
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-400/80">Overview</p>
            <h2 className="font-serif text-3xl text-neutral-50 sm:text-4xl">
              Designer bathroom presence. Developer-friendly economics.
            </h2>

            <div className="mt-8 space-y-5 text-neutral-300 leading-relaxed">
              <p>
                The ARIA Sculpt is a <strong>modern freestanding bathtub</strong> built around a
                single brief: deliver the visual gravity of a designer bathroom at a price point
                that protects your project margin. Cast from a high-density{" "}
                <strong>solid surface</strong> stone-resin composite, it is monolithic in
                appearance, soft to the touch, and engineered for the way real European
                bathrooms get specified, installed, and used.
              </p>
              <p>
                Where acrylic feels hollow and cast iron is logistically punishing, this{" "}
                <strong>solid surface bath</strong> sits in a quiet middle: warm under the hand,
                resilient under daily use, and visually heavy enough to anchor a primary suite,
                a hotel guest bathroom, or a high-end rental listing. The sculpted exterior and
                seamless interior are precision-machined as a single form — no visible seams, no
                chrome edge banding, no plastic feel.
              </p>
              <p>
                For property developers building at scale, the value is straightforward. A
                freestanding tub photographs better than a built-in. Better photography drives
                faster lease-up, stronger off-plan sales, and higher perceived value per square
                metre. The ARIA Sculpt was sourced specifically because it delivers that
                photograph — the one your marketing team needs — without the European luxury
                tax. This is{" "}
                <strong>affordable luxury</strong> in its most defensible form: the buyer feels
                the upgrade, your cost-per-unit doesn&apos;t flinch.
              </p>
              <p>
                For interior designers and architects, the silhouette is intentionally quiet.
                The form does the work, so the rest of the room can breathe. It pairs cleanly
                with stone slab walls, micro-cement floors, walnut joinery, and brushed brass
                or matte black brassware. It belongs in the same conversation as the Italian
                and Scandinavian references your clients send through — and looks the part in
                the renders.
              </p>
              <p>
                Installation is deliberately specifier-friendly. Standard EU plumbing rough-in,
                concealed centre pop-up drain, no structural reinforcement required for typical
                concrete slabs. The unit ships protected for container freight and arrives in a
                single piece — no on-site assembly, no surprise components.
              </p>
              <p>
                Together with the rest of our <strong>luxury bathroom</strong> range — basins,
                vanity consoles, and the REM7 smart toilet flagship — the ARIA Sculpt forms
                the centrepiece of a complete <strong>designer bathroom</strong> package built
                for serious projects.
              </p>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-sm border border-amber-500/20 bg-black/30 p-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400/80">
                Quick Spec
              </p>
              <ul className="mt-4 space-y-3 text-sm text-neutral-300">
                <li className="flex justify-between gap-4 border-b border-neutral-800 pb-2">
                  <span className="text-neutral-500">Type</span>
                  <span>Freestanding</span>
                </li>
                <li className="flex justify-between gap-4 border-b border-neutral-800 pb-2">
                  <span className="text-neutral-500">Material</span>
                  <span>Solid Surface</span>
                </li>
                <li className="flex justify-between gap-4 border-b border-neutral-800 pb-2">
                  <span className="text-neutral-500">Length</span>
                  <span>1700 mm</span>
                </li>
                <li className="flex justify-between gap-4 border-b border-neutral-800 pb-2">
                  <span className="text-neutral-500">Finish</span>
                  <span>Matte White</span>
                </li>
                <li className="flex justify-between gap-4">
                  <span className="text-neutral-500">MOQ</span>
                  <span>20+ units</span>
                </li>
              </ul>
              <Button
                asChild
                className="mt-6 w-full bg-gradient-to-r from-amber-500 to-amber-600 text-black hover:from-amber-400 hover:to-amber-500"
              >
                <a href="#quote">Request Bulk Pricing</a>
              </Button>
            </div>

            <ReservedImageSlot
              src="/images/bath/aria-sculpt-detail.jpg"
              alt="ARIA Sculpt solid surface bathtub detail — fluted exterior modern bathroom"
              aspect="aspect-square"
              slotLabel="Detail shot — fluted exterior"
            />
          </aside>
        </div>
      </section>

      {/* SWIPEABLE GALLERY */}
      <section id="gallery" className="scroll-mt-24 border-b border-amber-500/10 bg-black/40 py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="mb-10 max-w-2xl">
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-400/80">
              Gallery
            </p>
            <h2 className="font-serif text-3xl text-neutral-50 sm:text-4xl">
              See it in the room.
            </h2>
            <p className="mt-3 text-neutral-400">
              Swipe through silhouette, detail, and lifestyle scenes. Tap any image to open full screen.
            </p>
          </div>

          <SwipeableGallery images={galleryImages} />
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="border-b border-amber-500/10 bg-gradient-to-b from-neutral-950 via-black to-neutral-950 py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="mx-auto max-w-3xl text-center">
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-400/80">
              Why Choose ARIA Sculpt
            </p>
            <h2 className="font-serif text-3xl text-neutral-50 sm:text-4xl">
              Specified by people who think in <span className="text-amber-300">unit economics</span>.
            </h2>
            <p className="mt-4 text-neutral-400">
              Six reasons developers, designers, and architects keep coming back to this tub.
            </p>
          </div>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {whyChoose.map((f) => (
              <div
                key={f.title}
                className="rounded-sm border border-neutral-800 bg-neutral-950/60 p-6 transition-colors hover:border-amber-500/30"
              >
                <f.icon className="h-6 w-6 text-amber-400" />
                <h3 className="mt-4 font-serif text-lg text-neutral-100">{f.title}</h3>
                <p className="mt-2 text-sm text-neutral-400">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPECS */}
      <section id="specs" className="scroll-mt-24 border-b border-amber-500/10 py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="mb-10 max-w-2xl">
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-400/80">
              Technical Specifications
            </p>
            <h2 className="font-serif text-3xl text-neutral-50 sm:text-4xl">
              Built to spec. Documented to specify.
            </h2>
          </div>

          <div className="overflow-hidden rounded-sm border border-neutral-800">
            <table className="w-full text-sm">
              <tbody>
                {specs.map((row, i) => (
                  <tr
                    key={row.label}
                    className={i % 2 === 0 ? "bg-neutral-950" : "bg-black/40"}
                  >
                    <th
                      scope="row"
                      className="w-1/3 border-b border-neutral-800/60 px-6 py-4 text-left font-mono text-[11px] uppercase tracking-[0.15em] text-neutral-500"
                    >
                      {row.label}
                    </th>
                    <td className="border-b border-neutral-800/60 px-6 py-4 text-neutral-200">
                      {row.value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-4 text-xs text-neutral-500">
            Full installation drawing and CAD files available on request for specified projects.
          </p>
        </div>
      </section>

      {/* PERFECT FOR */}
      <section className="border-b border-amber-500/10 bg-black/40 py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="mb-12 max-w-2xl">
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-400/80">
              Perfect For
            </p>
            <h2 className="font-serif text-3xl text-neutral-50 sm:text-4xl">
              Where this bathtub earns its place.
            </h2>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {perfectFor.map((p) => (
              <div
                key={p.title}
                className="rounded-sm border border-amber-500/20 bg-neutral-950/60 p-6"
              >
                <p.icon className="h-6 w-6 text-amber-400" />
                <h3 className="mt-4 font-serif text-lg text-neutral-100">{p.title}</h3>
                <p className="mt-2 text-sm text-neutral-400">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTERNAL LINKS / RELATED */}
      <section className="border-b border-amber-500/10 py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-400/80">
                Pair It With
              </p>
              <h2 className="font-serif text-3xl text-neutral-50 sm:text-4xl">
                Complete the bathroom
              </h2>
            </div>
            <LocalizedLink
              to="/bath-sanitary"
              className="text-sm text-amber-300 hover:text-amber-200"
            >
              View full Bath &amp; Sanitary collection →
            </LocalizedLink>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <LocalizedLink to="/bath-sanitary/aria-monolith" className="group">
              <ReservedImageSlot
                src="/images/bath/aria-monolith.jpg"
                alt="ARIA Monolith solid surface bathtub architectural oval freestanding"
                aspect="aspect-[4/5]"
                slotLabel="ARIA Monolith Tub"
              />
              <div className="mt-4">
                <h3 className="font-serif text-lg text-neutral-100 group-hover:text-amber-200">
                  ARIA Monolith Tub
                </h3>
                <p className="text-sm text-neutral-500">Architectural oval solid surface variant</p>
              </div>
            </LocalizedLink>

            <LocalizedLink to="/bath-sanitary/vena-basin" className="group">
              <ReservedImageSlot
                src="/images/bath/vena-basin.jpg"
                alt="VENA solid surface counter basin luxury washbasin Europe"
                aspect="aspect-[4/5]"
                slotLabel="VENA Counter Basin"
              />
              <div className="mt-4">
                <h3 className="font-serif text-lg text-neutral-100 group-hover:text-amber-200">
                  VENA Counter Basin
                </h3>
                <p className="text-sm text-neutral-500">Sculpted solid surface washbasin</p>
              </div>
            </LocalizedLink>

            <LocalizedLink to="/bath-sanitary/vena-vanity" className="group">
              <ReservedImageSlot
                src="/images/bath/vena-vanity.jpg"
                alt="VENA Vanity Console integrated solid surface bathroom vanity"
                aspect="aspect-[4/5]"
                slotLabel="VENA Vanity Console"
              />
              <div className="mt-4">
                <h3 className="font-serif text-lg text-neutral-100 group-hover:text-amber-200">
                  VENA Vanity Console
                </h3>
                <p className="text-sm text-neutral-500">Integrated solid surface vanity solution</p>
              </div>
            </LocalizedLink>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="border-b border-amber-500/10 bg-black py-16">
        <div className="container mx-auto grid items-center gap-6 px-6 lg:grid-cols-3 lg:px-12">
          <div className="flex items-center gap-4 rounded-sm border border-amber-500/20 bg-neutral-950 p-5">
            <ShieldCheck className="h-10 w-10 text-amber-400" />
            <div>
              <p className="font-serif text-lg text-neutral-100">CE Certified</p>
              <p className="text-xs text-neutral-500">EN 14516 compliant solid surface bathware</p>
            </div>
          </div>
          <div className="flex items-center gap-4 rounded-sm border border-amber-500/20 bg-neutral-950 p-5">
            <Ruler className="h-10 w-10 text-amber-400" />
            <div>
              <p className="font-serif text-lg text-neutral-100">5 Year Warranty</p>
              <p className="text-xs text-neutral-500">Structural — built for long-life specification</p>
            </div>
          </div>
          <div className="flex items-center gap-4 rounded-sm border border-amber-500/20 bg-neutral-950 p-5">
            <Truck className="h-10 w-10 text-amber-400" />
            <div>
              <p className="font-serif text-lg text-neutral-100">EU Logistics</p>
              <p className="text-xs text-neutral-500">Sea freight 30–40 days · Air 10–15 days</p>
            </div>
          </div>
        </div>
      </section>

      {/* LEAD CAPTURE */}
      <section id="quote" className="scroll-mt-24 bg-gradient-to-b from-neutral-950 to-black py-24">
        <div className="container mx-auto grid gap-10 px-6 lg:grid-cols-2 lg:px-12">
          <div>
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-400/80">
              Project Enquiry
            </p>
            <h2 className="font-serif text-3xl text-neutral-50 sm:text-4xl">
              Get pricing for the ARIA Sculpt
            </h2>
            <p className="mt-4 max-w-md text-neutral-400">
              Tell us about your development. A project specialist will respond within one
              business day with bulk pricing, lead times, finish options, and OEM packaging
              if required.
            </p>
            <div className="mt-8 space-y-3 text-sm text-neutral-400">
              <p>
                📧{" "}
                <a className="text-amber-300 hover:underline" href="mailto:info@aureum7trading.com">
                  info@aureum7trading.com
                </a>
              </p>
              <p>
                📱{" "}
                <a className="text-amber-300 hover:underline" href="https://wa.me/34688495309">
                  +34 688 495 309 (WhatsApp)
                </a>
              </p>
            </div>
          </div>
          <BathLeadForm />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AriaSculpt;
